<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { BlockedExpectedVisit } from '@/api/planning/route-plan-expected-visits';

// Weekly-plan prefill strip for the visits step. Deliberately static: it keeps the same
// shape and position through the whole flow — only the status text and the CTA label
// change with the counts. "Cobertura del día" rides along as a text action; it is a
// read-only diagnostic, never the primary.
interface Props {
  expectedCount: number;
  newCount: number;
  loading: boolean;
  saving: boolean;
  // Fetch failure: the status must say so instead of a misleading "no hay visitas
  // esperadas" (an errored query reports count 0).
  error?: boolean;
  // Expected slots a patient schedule block leaves no room for: shown so the planner knows
  // why the count is short, never loadable.
  blocked?: BlockedExpectedVisit[];
}

withDefaults(defineProps<Props>(), { error: false, blocked: () => [] });
const emit = defineEmits<{ load: []; 'open-coverage': []; retry: [] }>();

const { t } = useI18n();

function blockedTimeLabel(visit: BlockedExpectedVisit): string {
  if (visit.fixedStartTime) return visit.fixedStartTime;
  if (visit.timeWindowStart && visit.timeWindowEnd) {
    return `${visit.timeWindowStart}–${visit.timeWindowEnd}`;
  }

  return t('routePlan.coverage.noTime');
}

function blockedLabel(visit: BlockedExpectedVisit): string {
  return `${visit.patientFullName} · ${blockedTimeLabel(visit)} · ${visit.scheduleBlock.description}`;
}
</script>

<template>
  <v-alert
    variant="tonal"
    color="primary"
    density="compact"
    icon="mdi-calendar-import"
    class="mb-4"
    data-testid="expected-strip"
  >
    <div class="d-flex align-center justify-space-between flex-wrap ga-3">
      <div class="text-body-2">
        <span class="font-weight-medium">{{ t('routePlan.expected.title') }}</span>
        <span :class="error && !loading ? 'text-error' : 'text-medium-emphasis'">
          ·
          <template v-if="loading">
            {{ t('routePlan.expected.loading') }}
          </template>
          <template v-else-if="error">
            {{ t('routePlan.expected.loadError') }}
          </template>
          <template v-else>
            {{ t('routePlan.expected.status', expectedCount) }}
          </template>
        </span>
      </div>
      <div class="d-flex align-center ga-2 flex-wrap">
        <v-btn
          v-if="error && !loading"
          size="small"
          variant="tonal"
          color="error"
          prepend-icon="mdi-refresh"
          @click="emit('retry')"
        >
          {{ t('routePlan.coverage.retry') }}
        </v-btn>
        <v-btn
          v-else-if="newCount > 0"
          size="small"
          variant="flat"
          color="primary"
          prepend-icon="mdi-download"
          :loading="loading"
          @click="emit('load')"
        >
          {{ t('routePlan.expected.loadCta', newCount) }}
        </v-btn>
        <span
          v-else-if="expectedCount > 0 && !loading"
          class="text-caption text-medium-emphasis"
        >
          <v-icon
            icon="mdi-check"
            size="small"
            color="success"
          />
          {{ t('routePlan.expected.allLoaded') }}
        </span>
        <v-btn
          size="small"
          variant="text"
          color="primary"
          prepend-icon="mdi-clipboard-check-outline"
          :loading="saving"
          @click="emit('open-coverage')"
        >
          {{ t('routePlan.coverage.cta') }}
        </v-btn>
      </div>
    </div>
    <div
      v-if="blocked.length > 0"
      class="mt-2"
      data-testid="expected-blocked"
    >
      <div class="text-caption text-medium-emphasis">
        <v-icon
          icon="mdi-calendar-lock"
          size="x-small"
          class="me-1"
        />
        {{ t('routePlan.expected.blocked', blocked.length) }}
      </div>
      <div class="d-flex flex-wrap ga-1 mt-1">
        <v-chip
          v-for="(visit, index) in blocked"
          :key="`${visit.patientId}-${index}`"
          size="x-small"
          variant="outlined"
          prepend-icon="mdi-calendar-lock"
        >
          {{ blockedLabel(visit) }}
        </v-chip>
      </div>
    </div>
  </v-alert>
</template>
